import { FormattedMessage } from '@alltrails/shared/react-intl';
// @ts-ignore
import LazyLoad from 'react-lazyload';
import classNames from 'classnames';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { Context } from '@alltrails/shared/types/context';
import InviteYourFriendsBanner from 'components/InviteYourFriendsBanner';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useDisplayMetric from 'hooks/useDisplayMetric';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { NUM_CURATED_TRAILS, LOGGED_DISTANCE, NUM_USERS } from 'constants/CommunityStats';
import * as styles from './styles/styles.module.scss';
import GenericGiftingBlock from '../blocks/GiftingBlock/GenericGiftingBlock';
import ShopifyPreview from '../blocks/ShopifyPreview';
import OnePercentBlock from '../blocks/OnePercentBlock';
import EditorsChoiceBlock from '../blocks/EditorsChoiceBlock';
import ExploreAndDiscover from '../blocks/ExploreAndDiscover';
import CommunityStats from '../blocks/CommunityStats/CommunityStats';
import TrailRiver from '../blocks/TrailRiver';
import SignupActivitySuitability from '../blocks/SignupActivitySuitability';
import useListItems from '../../hooks/useListItems';
import useSections from '../../hooks/useSections';
import PlusUpsell from '../blocks/PlusUpsell';
import PlusUpsellNoAuth from '../blocks/PlusUpsellNoAuth';
import ActivitiesRiver from '../blocks/ActivitiesRiver';
import SeoTrailTabbed from '../blocks/SeoTrailTabbed';
import { wrapUrlSafe } from '../../utils/language_support_util';
import {
  ATTRIBUTE,
  COMMUNITY_STATS,
  EDITORS_CHOICE_APP,
  EXPLORE_AND_DISCOVER,
  GIFTING_UPSELL,
  MERCHANDISE_STORE,
  ON_TRACK_WITH_PRO_CHECKLIST,
  ON_TRACK_WITH_PRO,
  ONE_PERCENT_PLANET,
  SEO_TRAILS,
  SIGNUP_ACTIVITY_SUITABILITY,
  TRAIL_LIST
} from './constants';
import { US } from '../../constants/CountryCode';

type Props = {
  context: Context;
  initialSections: {
    section_type?: string;
  }[];
  windowWidth?: number;
  lists: {
    listItems?: object;
    lists?: {
      id?: number;
      title?: string;
    }[];
  };
  locationData: {
    city: {
      names: { [key: string]: string };
    };
    country: string;
    latLng: (string | number)[];
  };
};

enum SectionType {
  Attribute = 'attribute',
  InviteYourFriends = 'invite_your_friends'
}

const SectionSwitch = ({ context, initialSections, windowWidth, lists, locationData }: Props) => {
  const displayMetric = useDisplayMetric();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const { listMethods, renderModal, renderCollabListPrivacyModal } = useListItems(lists, PageStrings.HOMEPAGE);
  const sections = useSections(initialSections, locationData.latLng, context, lists);

  const trailRiverTitle = (section: any, idx: any) => {
    if (idx === 0 && locationData.city && section.river_section_type !== 'personalized_trail_list') {
      const shortRegionCode = languageRegionCode.split('-')[0];

      return (
        <>
          <FormattedMessage defaultMessage="Local favorites near" />{' '}
          <a className="xlate-none" href={wrapUrlSafe('/explore?ref=homepage_explore_trailriver', languageRegionCode)}>
            {locationData.city.names[shortRegionCode]}
          </a>
        </>
      );
    }

    return section.title;
  };

  return (
    <>
      {renderModal()}
      {renderCollabListPrivacyModal()}
      {sections.map((section: any, idx: number) => {
        switch (section.section_type) {
          case SectionType.InviteYourFriends:
            return <InviteYourFriendsBanner key={section.section_type} />;
          case ATTRIBUTE:
            return <ActivitiesRiver key={section.section_type} title={section.title} activities={section.objects} windowWidth={windowWidth} />;
          case COMMUNITY_STATS:
            return (
              <CommunityStats
                key={section.section_type}
                curatedTrails={NUM_CURATED_TRAILS}
                fellowExplorers={NUM_USERS}
                loggedDistanceMeters={LOGGED_DISTANCE}
              />
            );
          case ON_TRACK_WITH_PRO_CHECKLIST:
            return <PlusUpsell key={section.section_type} />;
          case ON_TRACK_WITH_PRO:
            return <PlusUpsellNoAuth key={section.section_type} />;
          case GIFTING_UPSELL:
            return <GenericGiftingBlock key={section.section_type} />;
          case MERCHANDISE_STORE:
            return (
              locationData.country === US &&
              languageRegionCode === LanguageRegionCode.English && (
                <LazyLoad key={section.section_type}>
                  <ShopifyPreview shopifyProducts={section.products} windowWidth={windowWidth} key={section.section_type} />
                </LazyLoad>
              )
            );
          case ONE_PERCENT_PLANET:
            return <OnePercentBlock key={section.section_type} />;
          case EDITORS_CHOICE_APP:
            return <EditorsChoiceBlock key={section.section_type} />;
          case EXPLORE_AND_DISCOVER:
            return <ExploreAndDiscover key={section.section_type} windowWidth={windowWidth} />;
          case SEO_TRAILS:
            return <SeoTrailTabbed key={section.section_type} data={section.data} displayMetric={displayMetric} />;
          case SIGNUP_ACTIVITY_SUITABILITY:
            return (
              <SignupActivitySuitability
                activities={section.objects}
                key={section.section_type}
                mobileBrowser={__AT_DATA__.mobileBrowser}
                windowWidth={windowWidth}
              />
            );
          case TRAIL_LIST:
            return (
              <TrailRiver
                {...section}
                context={context}
                windowWidth={windowWidth}
                listMethods={listMethods}
                className={classNames(styles.trailRiverPadding, { [styles.authenticatedSpacing]: user && idx === 0 })}
                locationData={locationData}
                title={trailRiverTitle(section, idx)}
                key={section.title}
                riverAnalyticsData={{
                  riverIndex: idx,
                  filters: section.filters,
                  riverSectionType: section.river_section_type,
                  riverTitleFilter: section.river_title_filter,
                  riverTitle: section.title
                }}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

SectionSwitch.defaultProps = {
  windowWidth: null
};

export default SectionSwitch;
